import { getRESTParams, postRESTParams, REMBLE_API_URL } from '.'
import { queryStringFromObj } from '../helpers/Strings'

const BASE_PATH = `${REMBLE_API_URL}/users/bookmarks`

const BOOKMARK_KEY_CATEGORY = 'category'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common User Bookmarks GET request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See getRESTParams method for more info.
 */
const _userBookmarksRESTGET = async (url, params) => {
  const result = await fetch(url, getRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Common User Bookmarks POST request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _userBookmarksRESTPOST = async (url, params) =>
  _userBookmarksRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/**
 * Common User Bookmarks DELETE request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _userBookmarksRESTDELETE = async (url, params) =>
  _userBookmarksRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Retrieves user bookmarks for the active user that match the passed in queryParam criteria or an empty array
 * @param {Object} queryParams Query parameters to pass to `/find` endpoint
 */
const getUserBookmarks = async ({
  category,
  category_object_id,
  limit = 10
}) => {
  let url = `${BASE_PATH}/find`
  let queryObj = {
    limit
  }

  // Append the category to the payload if we have one
  if (category) queryObj['category'] = category

  // Append the category_object_id to the payload if we have one
  if (category_object_id) queryObj['category_object_id'] = category_object_id

  url += queryStringFromObj(queryObj)

  const response = await _userBookmarksRESTGET(url, getRESTParams(queryObj))
  return response.bookmarks ? response.bookmarks : []
}

/**
 * Posts user bookmark data to the /users/bookmarks/create endpoint to save user bookmarks
 * @param {String} category The bookmark category (e.g., "affirmation", "journal", "lesson", "topic", etc.)
 * @returns Returns an object with an error or an object with the post response data
 */
const saveUserBookmark = async payload => {
  const url = `${BASE_PATH}/create`

  const body = JSON.stringify(payload)

  const saved = await _userBookmarksRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return saved
}

/**
 * Posts user bookmark data to the /users/bookmarks/delete endpoint to delete user bookmarks
 * @param {int} id The bookmark id (e.g., 1,2,3, etc.)
 * @returns Returns an object with an error or an object with the post response data
 */
const deleteUserBookmark = async id => {
  const url = `${BASE_PATH}/delete/${id}`

  const deleted = await _userBookmarksRESTDELETE(url, {
    method: 'DELETE'
  }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return deleted
}

export {
  getUserBookmarks,
  saveUserBookmark,
  deleteUserBookmark,
  BOOKMARK_KEY_CATEGORY
}
